import { supabase } from './supabase';
import { toSafeUiMessage } from './dashboard-errors';

const normalizeWorkerUrl = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (trimmed.length === 0) return null;
  return trimmed.replace(/\/+$/, '');
};

export const runFlowWorkerUrl = normalizeWorkerUrl(import.meta.env.VITE_RUN_FLOW_URL);

const parseJsonOrText = async (response: Response): Promise<unknown> => {
  const text = await response.text();
  if (text.trim().length === 0) return null;

  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
};

const extractErrorMessage = (payload: unknown, fallback: string): string => {
  if (typeof payload === 'string' && payload.trim().length > 0) return payload;
  if (typeof payload !== 'object' || payload === null) return fallback;

  const record = payload as Record<string, unknown>;
  const error = record.error;
  if (typeof error === 'string' && error.trim().length > 0) return error;

  return fallback;
};

export const invokeRunFlowRuntime = async (body: Record<string, unknown>): Promise<unknown> => {
  if (!runFlowWorkerUrl) {
    throw new Error(
      'Runtime worker non configuré. Définis VITE_RUN_FLOW_URL pour lancer ce tool hors Supabase Edge.'
    );
  }

  return invokeAuthenticatedRuntime(runFlowWorkerUrl, body);
};

export const invokeAuthenticatedRuntime = async (url: string, body: Record<string, unknown>): Promise<unknown> => {
  const { data: { session } } = await supabase.auth.getSession();
  const accessToken = session?.access_token;
  if (!accessToken) {
    throw new Error('Session expirée. Reconnecte-toi.');
  }

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      authorization: `Bearer ${accessToken}`,
      'content-type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  const payload = await parseJsonOrText(response);
  if (!response.ok) {
    throw new Error(toSafeUiMessage(extractErrorMessage(payload, 'Le service est indisponible. Votre travail est conservé.'), 'Le service est indisponible. Votre travail est conservé.'));
  }

  return payload;
};
